import { useFeedbackHost } from "../feedback/useFeedbackHost.js";
import { AlertTitle, Box } from "@mui/material";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { FeedbackAlert, useFeedbackTokens } from "../feedback/FeedbackAlert.js";
import {
  enqueueFeedback,
  feedbackDuration,
  type FeedbackEntry,
  type FeedbackSeverity,
} from "../feedback/feedbackQueue.js";
import dico from "../../../util/src/language/dico.js";

export interface SnackOptions {
  duration?: number | null;
  title?: string;
  action?: ReactNode;
  details?: string;
}
interface SnackContextType {
  /** Existing two- and three-argument callers remain supported. null keeps the message visible. */
  showSnack: (
    message: string,
    severity: FeedbackSeverity,
    options?: number | null | SnackOptions,
  ) => void;
}
interface Snack extends FeedbackEntry {
  options: SnackOptions;
}
const SnackContext = createContext<SnackContextType | undefined>(undefined);

function SnackItem({
  snack,
  dismiss,
}: {
  snack: Snack;
  dismiss: (id: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const duration = feedbackDuration(
    snack.severity,
    snack.options.duration === undefined && snack.options.action
      ? null
      : snack.options.duration,
  );
  const remaining = useRef(duration);
  const started = useRef(0);
  // A coalesced occurrence starts a fresh timeout without removing/re-adding its live region.
  useEffect(() => {
    remaining.current = duration;
  }, [duration, snack.revision]);
  useEffect(() => {
    if (duration === null || hovered || focused || detailsOpen) return;
    started.current = Date.now();
    const timer = setTimeout(
      () => dismiss(snack.id),
      Math.max(0, remaining.current ?? duration),
    );
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(
        0,
        (remaining.current ?? duration) - (Date.now() - started.current),
      );
    };
  }, [
    duration,
    hovered,
    focused,
    detailsOpen,
    snack.id,
    snack.revision,
    dismiss,
  ]);
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setFocused(false);
      }}
      sx={{ pointerEvents: "auto", minWidth: 0, flexShrink: 0 }}
    >
      <FeedbackAlert
        severity={snack.severity}
        data-feedback-toast=""
        closeText={dico().close}
        onClose={() => dismiss(snack.id)}
      >
        {snack.options.title && <AlertTitle>{snack.options.title}</AlertTitle>}
        <Box
          component="span"
          sx={{ fontWeight: snack.options.title ? 400 : 600 }}
        >
          {snack.message}
        </Box>
        {snack.count > 1 && (
          <Box
            component="span"
            sx={{ ml: 1, fontVariantNumeric: "tabular-nums" }}
          >
            ×{snack.count}
          </Box>
        )}
        {snack.options.action && (
          <Box sx={{ mt: 1 }}>{snack.options.action}</Box>
        )}
        {snack.options.details && (
          <Box
            component="details"
            onToggle={(event) =>
              setDetailsOpen(event.currentTarget.hasAttribute("open"))
            }
            sx={{ mt: 1, fontSize: 11, whiteSpace: "pre-wrap" }}
          >
            <summary>{dico().errors.criticalBlock.detailsLabel}</summary>
            <Box sx={{ mt: 1 }}>{snack.options.details}</Box>
          </Box>
        )}
      </FeedbackAlert>
    </Box>
  );
}

export const SnackProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<Snack[]>([]);
  const nextId = useRef(0);
  const host = useFeedbackHost(queue.length > 0);
  const p = useFeedbackTokens();
  const showSnack = useCallback(
    (
      message: string,
      severity: FeedbackSeverity,
      input?: number | null | SnackOptions,
    ) => {
      const options: SnackOptions =
        typeof input === "number"
          ? { duration: input }
          : input === null
            ? { duration: null }
            : (input ?? {});
      const next: Snack = {
        id: ++nextId.current,
        message,
        severity,
        options,
        count: 1,
        revision: 0,
        coalescible: !options.title && !options.action && !options.details,
      };
      setQueue((previous) =>
        enqueueFeedback(
          previous,
          next,
          !options.title && !options.action && !options.details,
        ),
      );
    },
    [],
  );
  const dismiss = useCallback(
    (id: number) =>
      setQueue((previous) => previous.filter((item) => item.id !== id)),
    [],
  );
  const contextValue = React.useMemo(() => ({ showSnack }), [showSnack]);
  return (
    <SnackContext.Provider value={contextValue}>
      {children}
      {host &&
        queue.length > 0 &&
        createPortal(
          <Box
            data-feedback-stack=""
            sx={{
              position: "fixed",
              right: 24,
              bottom: 24,
              width: 368,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "calc(100dvh - 48px)",
              overflowY: "auto",
              overflowX: "hidden",
              boxSizing: "border-box",
              zIndex: (theme) => theme.zIndex.snackbar,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pointerEvents: "none",
              color: p.text,
              "@media (max-width:600px)": { right: 16, bottom: 16 },
            }}
          >
            {queue.slice(0, 2).map((snack) => (
              <SnackItem key={snack.id} snack={snack} dismiss={dismiss} />
            ))}
          </Box>,
          host,
        )}
    </SnackContext.Provider>
  );
};
export const useSnack = (): SnackContextType => {
  const context = useContext(SnackContext);
  if (!context) throw new Error("useSnack must be used within a SnackProvider");
  return context;
};
